import * as React from 'react';
import { Filespace, FilespaceExtended } from 'fs-api-client';
import { capitalizeFirstLetter, copyToClipboard, formatDataSize } from '../../../webportal/utils/utilFunctions';
import { getFilespaceItemStyle } from '../../utils/utilFunctions';
import { HubInfo } from '../common/HubInfo';
import { renderCopyIcon } from '../../../webportal/components/common/Icons';
import { BackOfficeEvent } from '../common/BackOfficeEvents';
import { FilespaceDetailsModal } from './FilespaceDetailsModal';
import grafanaImgUrl from 'src/webportal/theme/images/grafana.png';
import kibanaImgUrl from 'src/webportal/theme/images/kibana.png';
import { renderFilespaceTypeSpan } from '../common/Filespace';
import { authenticationService } from '../../../webportal/services/authentication';
import { ConfirmationModal } from '../billing/BillingPlanConfirmModal';

export enum FilespaceStatus {
    WaitingForHubAssignment = 'WaitingForHubAssignment',
    WaitingForStorage = 'WaitingForStorage',
    Provisioning = 'Provisioning',
    ReadyForInitialization = 'ReadyForInitialization',
    Initialized = 'Initialized',
    Errored = 'Errored'
}

enum FilespaceSuspensionStatusString {
    Suspending = 'suspending',
    Suspended = 'suspended',
    SuspensionFailed = 'suspension_failed',
    Unsuspending = 'unsuspending',
    UnsuspensionFailed = 'unsuspension_failed',
    NotSuspended = 'not_suspended',
    WaitingForUnsuspension = 'waiting_for_unsuspension',
    WaitingForSuspension = 'waiting_for_suspension'
}

enum FilespaceSuspensionStatus {
    Suspending = 'Suspending',
    Suspended = 'Suspended',
    SuspensionFailed = 'SuspensionFailed',
    Unsuspending = 'Unsuspending',
    UnsuspensionFailed = 'UnsuspensionFailed',
    NotSuspended = 'NotSuspended',
    WaitingForUnsuspension = 'WaitingForUnsuspension',
    WaitingForHubAssignment = 'WaitingForHubAssignment',
    WaitingForBackupRestoration = 'WaitingForBackupRestoration',
    Provisioning = 'Provisioning',
    WaitingForSuspension = 'WaitingForSuspension'

}

enum FilespaceHeaders {
    NoUsers = 'No users',
    NoStats = 'No filespace stats',
    NoType = 'No filespace type'
}

interface BackOfficeFilespaceDetailsProps {
    domainName: string;
    filespace?: FilespaceExtended;
    index?: number;
    securityScopes?: string [];
}

interface BackOfficeFilespaceDetailsState {
    isCopyButtonClicked: boolean;
    isModalShown: boolean;
    isShowButtonClicked: boolean;
    isBillingEnabled: boolean | null;
    authToken?: string;
    isToggleBillingModalShown: boolean;

}

const loading = (
    <svg
        version="1.1"
        id="L4"
        x="0px"
        y="0px"
        viewBox="0 0 100 100"
        enable-background="new 0 0 0 0"
        style={{ width: '1.5rem' }}
    >
        <circle fill="#666" stroke="none" cx="6" cy="50" r="6">
            <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.1"
            />
        </circle>
        <circle fill="#666" stroke="none" cx="26" cy="50" r="6">
            <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.2"
            />
        </circle>
        <circle fill="#666" stroke="none" cx="46" cy="50" r="6">
            <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.3"
            />
        </circle>
    </svg>
);

export class FilespaceDetails extends React.Component<BackOfficeFilespaceDetailsProps, BackOfficeFilespaceDetailsState> {
    constructor(props: BackOfficeFilespaceDetailsProps) {
        super(props);
        this.state = {
            isCopyButtonClicked: false,
            isModalShown: false,
            isShowButtonClicked: false,
            isBillingEnabled: null,
            isToggleBillingModalShown: false,
        };

        this.showModal = this.showModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.getSuspensionStatusString = this.getSuspensionStatusString.bind(this);
    }

    componentDidMount() {
        document.addEventListener(BackOfficeEvent.CloseModal, this.closeModal);
        this.fetchIsBillingEnabled();
    }

    componentDidUpdate(prevProps: Readonly<BackOfficeFilespaceDetailsProps>) {
        if (this.props.filespace !== prevProps.filespace) {
            this.fetchIsBillingEnabled();
        }
    }

    rpc = async (endpoint: string = '', options: object = {}) =>
        this.props.filespace && !this.props.filespace.isDeleted ?
            fetch(
                `/api/v1/admin/isBillingEnabled?filespaceId=${this.props.filespace?.id}${endpoint}`,
                {
                    ...options,
                    headers: {
                        ...((options as any).headers ?? {}),
                        Authorization: this.state.authToken ?? (
                            await authenticationService.getIdToken().then(authToken => {
                                this.setState({ authToken });
                                return authToken;
                            })
                        )
                    }
                }
            ) : null

    fetchIsBillingEnabled = () => {
        this.setState({ isBillingEnabled: null });
        this.rpc()
            .then((r) => r?.json() ?? null)
            .then((isBillingEnabled: boolean | null) => {
                this.setState({ isBillingEnabled });
            })
            .catch((e) => console.error(e));
    }

    render() {
        const filespace = this.props.filespace;
        const domainName = this.props.domainName;
        if (!filespace) {
            return null;
        }
        const { isBillingEnabled } = this.state;
        const copyClassName = this.state.isCopyButtonClicked ? 'copy copied' : 'copy';
        let fullFilespaceName: string = `${filespace.name}.${domainName}`;
        return (
            <div className="row listItem" key={this.props.index} style={getFilespaceItemStyle(this.props.filespace!)}>
                <div className="fullWidth">
                    <div className="spaceBetween">
                        <div>
                            <h2>
                                {fullFilespaceName}
                                <a
                                    href="javascript:void(0);"
                                    className={copyClassName}
                                    title="Copy to clipboard"
                                    onClick={() => {
                                        this.setState({ isCopyButtonClicked: true });
                                        copyToClipboard(fullFilespaceName);
                                    }}
                                >
                                    {renderCopyIcon()}
                                </a>
                            </h2>
                            <ul className="details">
                                {this.renderFilespaceHeaderDetails(filespace)}
                            </ul>
                        </div>
                        <div className="buttons right">
                            <a href={filespace.grafanaLink} target="_blank">
                                <img src={grafanaImgUrl} alt="Grafana" title="Grafana"/>
                            </a>
                            <a href={filespace.kibanaLink} target="_blank">
                                <img src={kibanaImgUrl} alt="Kibana" title="Kibana"/>
                            </a>
                        </div>
                    </div>
                    <div className="description spaceBetween">
                        <div>
                            <ul className="details">
                                {filespace.stats &&
                                    <li>
                                        Used storage:&nbsp;
                                        <span>
                                            {formatDataSize(filespace.stats.usedStorage)}
                                        </span>
                                    </li>
                                }
                            </ul>
                            <ul className="details">
                                {filespace.stats &&
                                    <li>
                                        File system size:&nbsp;
                                        <span>
                                            {formatDataSize(filespace.stats.filespaceSize)}
                                        </span>
                                    </li>
                                }
                            </ul>
                            <ul className="details">
                                <li>Created on {new Date(filespace.createdTime!).toDateString()}</li>
                                {filespace.stats && (
                                    <li><a href="javascript:void(0);" onClick={this.showModal}>Details</a></li>
                                )}
                            </ul>
                            {filespace.id &&
                                <ul className="details">
                                    <li>Mongo ID: {filespace.id}</li>
                                </ul>}
                            {filespace.fsId &&
                                <ul className="details">
                                    <li>Filespace UUID: {filespace.fsId}</li>
                                </ul>}
                            {!filespace.isDeleted &&
                                <ul className="details">
                                    <HubInfo filespaceName={filespace.name} domainName={domainName}/>
                                </ul>}
                            {!filespace.isDeleted && 
                                <ul className="details">
                                    {isBillingEnabled === null ? (
                                        <li>{loading}</li>
                                    ) : (
                                        <li>
                                            Billing is{' '}
                                            {isBillingEnabled ? 'En' : 'Dis'}abled
                                            {this.props.securityScopes?.some(
                                                (scope) => scope === 'billingManage' || scope === 'master'
                                            ) && (
                                                <>
                                                    :{' '}
                                                    <a
                                                        href="javascript:void(0)"
                                                        onClick={() =>
                                                            this.setState({
                                                                isToggleBillingModalShown:
                                                                    true,
                                                            })
                                                        }
                                                    >
                                                        {isBillingEnabled ? 'Dis' : 'En'}able
                                                    </a>
                                                </>
                                            )}
                                        </li>
                                    )}
                                </ul>
                            }
                        </div>
                        {filespace.status && this.renderSuspensionStatus(filespace)}

                        {this.state.isModalShown && filespace.stats &&
                            <FilespaceDetailsModal
                                filespaceStats={filespace.stats}
                                filespace={filespace}
                                isModalShown={this.state.isModalShown}
                                fullFilespaceName={fullFilespaceName}
                            />
                        }

                        <ConfirmationModal
                            isModalShown={this.state.isToggleBillingModalShown}
                            confirmationText={`${isBillingEnabled ? 'Dis' : 'En'}able billing for ${filespace?.name}.${domainName}?`}
                            onConfirm={() => {
                                this.setState({
                                    isBillingEnabled: null,
                                    isToggleBillingModalShown: false,
                                });

                                this.rpc(
                                    `&isBillingEnabled=${JSON.stringify(!isBillingEnabled)}`,
                                    { method: 'PATCH' }
                                )
                                    .then(() => this.setState({ isBillingEnabled: !isBillingEnabled }));
                            }}
                            onClose={() => this.setState({ isToggleBillingModalShown: false })}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private showModal(): void {
        this.setState({ isModalShown: true });
    }

    private closeModal(): void {
        this.setState({ isModalShown: false });
    }

    private renderFilespaceHeaderDetails(filespace: FilespaceExtended): JSX.Element {
        return (
            <React.Fragment>
                {filespace.subscriptionType && <li>{capitalizeFirstLetter(filespace.subscriptionType.toString())}</li>}
                {!filespace.subscriptionType && <li className="inactive">{FilespaceHeaders.NoType}</li>}
                {filespace.storage.provider && filespace.storage.region &&
                    <li>{filespace.storage.provider} ({filespace.storage.region})</li>}
                {!filespace.storage.provider && filespace.storage.region &&
                    <li className="inactive">{filespace.storage.provider} ({filespace.storage.region})</li>}
                {!filespace.stats && <li className="inactive">{FilespaceHeaders.NoStats}</li>}
                {filespace.usersCount > 0 && <li>Users: {filespace.usersCount}</li>}
                {!filespace.usersCount && <li className="inactive">{FilespaceHeaders.NoUsers}</li>}
                {filespace.version && <li>ver. {filespace.version}</li>}
            </React.Fragment>
        );
    }

    private renderSuspensionStatus(filespace: FilespaceExtended): JSX.Element | null {
        let message = '';
        const suspensionStatus = this.getSuspensionStatusString(filespace.suspensionStatus);
        if (suspensionStatus === FilespaceSuspensionStatus.WaitingForUnsuspension) {
            message = 'Waiting for unsuspension';
        }

        if (
            [this.getSuspensionStatusString(FilespaceSuspensionStatus.WaitingForHubAssignment as any),
                this.getSuspensionStatusString(FilespaceSuspensionStatus.Provisioning as any),
                this.getSuspensionStatusString(FilespaceSuspensionStatus.WaitingForHubAssignment as any)].includes(suspensionStatus as any) ||
            suspensionStatus === FilespaceSuspensionStatus.Unsuspending
        ) {
            message = 'Unsuspending';
        }

        if (suspensionStatus === Filespace.SuspensionStatusEnum.UnsuspensionFailed.toString()) {
            message = 'Unsuspension failed';
        }

        if (suspensionStatus === Filespace.SuspensionStatusEnum.Suspended.toString()) {
            message = 'Suspended';
        }

        if (suspensionStatus === Filespace.SuspensionStatusEnum.Suspending.toString()) {
            message = 'Suspending';
        }

        if (suspensionStatus === Filespace.SuspensionStatusEnum.SuspensionFailed.toString()) {
            message = 'Suspension failed';
        }

        if (filespace.isDeleted) {
            if (filespace.deletionDate) {
                message = `Deleted ${new Date(filespace.deletionDate).toDateString()}`;
            } else {
                message = `Deleted`;
            }
        }

        return (
            <div className="right">
                <span className="warning">{message}</span>
                <br/>
                {this.renderFilespaceStatus(filespace)}
                <br/>
                {renderFilespaceTypeSpan(filespace)}
            </div>
        );
    }

    private renderFilespaceStatus(filespace: FilespaceExtended): JSX.Element | null {
        if (filespace.status) {
            const filespaceStatus = filespace.status.toString();
            if (filespaceStatus === FilespaceStatus.ReadyForInitialization) {
                return <span className="warning">Ready for initialization</span>;
            }
            if (filespaceStatus === FilespaceStatus.Initialized) {
                return <span className="active">Initialized</span>;
            }
            if (filespaceStatus === FilespaceStatus.WaitingForHubAssignment) {
                return <span className="warning">Waiting for hub assignment</span>;
            }
            if (filespaceStatus === FilespaceStatus.WaitingForStorage) {
                return <span className="warning">Waiting for storage</span>;
            }
            if (filespaceStatus === FilespaceStatus.Provisioning) {
                return <span className="warning">Provisioning</span>;
            }
        }
        return null;
    }

    // TODO Fix mapping. After the mapping is done, we need to go over the if statements, because they will be broken

    private getSuspensionStatusString(filespaceSuspensionStatus: Filespace.SuspensionStatusEnum | undefined): string {
        switch (filespaceSuspensionStatus && filespaceSuspensionStatus.toString()) {
            case FilespaceSuspensionStatus.WaitingForUnsuspension:
            case FilespaceSuspensionStatus.WaitingForHubAssignment:
            case FilespaceSuspensionStatus.WaitingForBackupRestoration:
                return FilespaceSuspensionStatusString.WaitingForUnsuspension;
            case FilespaceSuspensionStatus.WaitingForSuspension:
                return FilespaceSuspensionStatusString.WaitingForSuspension;
            case FilespaceSuspensionStatus.Suspending:
                return FilespaceSuspensionStatusString.Suspending;
            case FilespaceSuspensionStatus.Suspended:
                return FilespaceSuspensionStatusString.Suspended;
            case FilespaceSuspensionStatus.SuspensionFailed:
                return FilespaceSuspensionStatusString.SuspensionFailed;
            case FilespaceSuspensionStatus.Unsuspending:
                return FilespaceSuspensionStatusString.Unsuspending;
            case FilespaceSuspensionStatus.UnsuspensionFailed:
                return FilespaceSuspensionStatusString.UnsuspensionFailed;
            case FilespaceSuspensionStatus.NotSuspended:
                return FilespaceSuspensionStatusString.NotSuspended;
            default:
                return '';
        }
    }

}



// WEBPACK FOOTER //
// ./src/backoffice/components/filespace/FilespaceDetails.tsx